import { Button } from "@/components/ui/button";
import * as Card from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { api } from "@api";
import { DataGuard } from "@components/DataGuard";
import Identity from "@components/Identity";
import LayoutHeader from "@components/LayoutHeader";
import Panel from "@components/Panel";
import VideoPlayer from "@components/VideoPlayer";
import { A, useParams } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import dayjs from "dayjs";
import { ArrowLeft } from "lucide-solid";
import { Show, createMemo } from "solid-js";
import { Box, Grid } from "styled-system/jsx";

// 1. Import the generic component and your new adapter
import Comments from "@features/Comments";
import { recordingCommentsAdapter } from "./recordingCommentsAdapter";

export default function SingleRecording() {
  const params = useParams();

  const recordingQuery = useQuery(() => ({
    queryKey: ['recording', params.id],
    queryFn: async () => {
      const { data, error } = await api()
        .from('recordings')
        .select(`
          *,
          event:events(*),
          host:hosted_by (
            id,
            username,
            profile_private (role)
          )
        `)
        .eq('id', params.id)
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!params.id,
  }));

  const recordingTitle = createMemo(() => {
    const recording = recordingQuery.data;
    const event = recording?.event;
    return event?.group || recording?.group || 'Ad Hoc';
  });

  return (
    <DataGuard query={recordingQuery}>
      {(recording) => (
        <>
          <LayoutHeader
            title={recordingTitle()}
            subTitle={dayjs(recording?.occurrence_date).format('dddd, MMMM D, YYYY')}
            rightContent={
              <A href={'/recordings'}>
                <Button size='xs' variant='outline'>
                  <ArrowLeft />
                  Back to List
                </Button>
              </A>
            }
          />

          <Panel height="calc(var(--vh) * 100 - 102px)" overflowY="auto">
            <Box
              bg="black"
              borderRadius="lg"
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="center"
              maxHeight="calc(90dvh - 180px)"
              width="100%"
            >
              <VideoPlayer provider={recording?.provider} provider_id={recording?.provider_id} />
            </Box>

            <Grid gap='3' width="full" maxW="4xl" py="3" mx="auto">
              <Card.Root justifyContent='space-between' alignItems='start' p={3}>
                <Show when={recording?.description}>
                  <Text>{recording?.description}</Text>
                </Show>
                <Identity
                  username={recording?.host?.username}
                  id={recording?.host?.id}
                  label="Host"
                  rank={(recording?.host?.profile_private as any)?.role}
                  direction="row-reverse"
                />
              </Card.Root>

              {/* 2. Instantiate the generic Comments component with your adapter! */}
              <Comments
                entityId={recording?.id}
                adapter={recordingCommentsAdapter}
              />
            </Grid>
          </Panel>
        </>
      )}
    </DataGuard>
  );
}
